// src/components/blog/search/SearchFilters.tsx

'use client'

import RiFilterLine from '~/icons/remix/generated/RiFilterLine'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import { useState } from 'react'
import { AUTHORS, TOPICS, TAGS } from '~/lib/blog/config'
import { formatForDisplay } from '~/lib/blog/utils/formatting'
import { cn } from '~/lib/utils'

interface SearchFiltersProps {
  currentFilters: {
    author?: string
    topic?: string
    tag?: string
    startDate?: string
    endDate?: string
    q?: string
  }
  resultCount: number
  onFilterChange: (key: string, value: string | null) => void
  isLoading?: boolean
}

export function SearchFilters({ 
  currentFilters,
  resultCount,
  onFilterChange,
  isLoading = false
}: SearchFiltersProps): JSX.Element {
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  const activeFiltersCount = Object.values(currentFilters).filter(Boolean).length
  
  const renderFilterContent = (): JSX.Element => (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-4">Filter Results</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {resultCount} {resultCount === 1 ? 'result' : 'results'} found
          {activeFiltersCount > 0 && ` • ${activeFiltersCount} ${activeFiltersCount === 1 ? 'filter' : 'filters'} active`}
        </p>
      </div>

      {/* Topics Filter - Most important, so it goes first */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white p-6 dark:border-gray-800/60 dark:bg-gray-900">
        <h4 className="font-semibold mb-4 text-gray-900 dark:text-white">Topics</h4>
        <div className="space-y-3">
          {[...TOPICS].sort((a, b) => a.title.localeCompare(b.title)).map(topic => (
            <label
              key={topic.slug}
              className="flex items-center gap-x-3 text-sm"
            >
              <input
                type="checkbox"
                checked={currentFilters.topic === topic.slug}
                onChange={() => onFilterChange('topic', currentFilters.topic === topic.slug ? null : topic.slug)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 dark:border-gray-700 dark:bg-gray-800"
                disabled={isLoading}
              />
              <span className="text-gray-600 dark:text-gray-400">{topic.title}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Popular Tags */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white p-6 dark:border-gray-800/60 dark:bg-gray-900">
        <h4 className="font-semibold mb-4 text-gray-900 dark:text-white">Popular Tags</h4>
        <div className="flex flex-wrap gap-2">
          {TAGS.slice(0, 15).sort().map(tag => (
            <button
              key={tag}
              onClick={() => onFilterChange('tag', currentFilters.tag === tag ? null : tag)}
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors',
                currentFilters.tag === tag
                  ? 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-900/50 dark:text-blue-300 dark:ring-blue-500/30'
                  : 'bg-gray-50 text-gray-600 ring-gray-500/10 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-400/20 dark:hover:bg-gray-700'
              )}
              disabled={isLoading}
            >
              {formatForDisplay(tag)}
            </button>
          ))}
        </div>
      </div>

      {/* Date Range */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white p-6 dark:border-gray-800/60 dark:bg-gray-900">
        <h4 className="font-semibold mb-4 text-gray-900 dark:text-white">Date Range</h4>
        <form onSubmit={(e) => {
          e.preventDefault()
          const formData = new FormData(e.currentTarget)
          
          // Safely extract form values and convert to strings
          // FormData.get() returns FormDataEntryValue | null, which can be File | string | null
          // We need to handle this properly to avoid the base-to-string error
          const startDateValue = formData.get('startDate')
          const endDateValue = formData.get('endDate')
          
          // Convert to string safely - only strings are valid for date inputs
          // File objects would be invalid here since date inputs only return strings
          const startDateString = startDateValue && typeof startDateValue === 'string' ? startDateValue : null
          const endDateString = endDateValue && typeof endDateValue === 'string' ? endDateValue : null
          
          // Apply the date filters
          onFilterChange('startDate', startDateString)
          onFilterChange('endDate', endDateString)
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5">From</label>
            <input
              type="date"
              name="startDate"
              defaultValue={currentFilters.startDate ?? ''}
              className="block w-full rounded-lg border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:bg-gray-800 dark:text-white dark:ring-gray-700 dark:placeholder:text-gray-500 dark:focus:ring-blue-500 sm:text-sm sm:leading-6"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5">To</label>
            <input
              type="date"
              name="endDate"
              defaultValue={currentFilters.endDate ?? ''}
              className="block w-full rounded-lg border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:bg-gray-800 dark:text-white dark:ring-gray-700 dark:placeholder:text-gray-500 dark:focus:ring-blue-500 sm:text-sm sm:leading-6"
              disabled={isLoading}
            />
          </div>
          <div className="flex items-center justify-between">
            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              disabled={isLoading}
            >
              Apply Date Range
            </button>
            {(currentFilters.startDate ?? currentFilters.endDate) && (
              <button
                type="button"
                onClick={() => {
                  onFilterChange('startDate', null)
                  onFilterChange('endDate', null)
                }}
                className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                disabled={isLoading}
              >
                Clear dates
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Authors Filter - Less commonly used, so it goes last */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white p-6 dark:border-gray-800/60 dark:bg-gray-900">
        <h4 className="font-semibold mb-4 text-gray-900 dark:text-white">Authors</h4>
        <div className="space-y-3">
          {[...AUTHORS].sort((a, b) => a.name.localeCompare(b.name)).map(author => {
            const authorId = author.href.split('/').pop() ?? ''
            return (
              <label
                key={author.href}
                className="flex items-center gap-x-3 text-sm"
              >
                <input
                  type="checkbox"
                  checked={currentFilters.author === authorId}
                  onChange={() => onFilterChange('author', currentFilters.author === authorId ? null : authorId)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 dark:border-gray-700 dark:bg-gray-800"
                  disabled={isLoading}
                />
                <span className="text-gray-600 dark:text-gray-400">{author.name}</span>
              </label>
            )
          })}
        </div>
      </div>
    </div>
  )
  
  return (
    <>
      {/* Mobile filter button */}
      <div className="lg:hidden">
        <button
          type="button"
          className="fixed bottom-4 right-4 z-30 inline-flex items-center gap-x-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          onClick={() => setIsMobileFiltersOpen(true)}
          disabled={isLoading}
        >
          <RiFilterLine className="h-5 w-5" aria-hidden="true" />
          Filters
          {activeFiltersCount > 0 && (
            <span className="ml-1 rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-white">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {/* Mobile filter dialog */}
      <div
        className={cn(
          'fixed inset-0 z-50 lg:hidden',
          isMobileFiltersOpen ? '' : 'hidden'
        )}
        role="dialog"
        aria-modal="true"
      >
        {/* Background backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm dark:bg-opacity-40"
          aria-hidden="true"
          onClick={() => setIsMobileFiltersOpen(false)}
        />

        {/* Dialog panel */}
        <div className="fixed inset-0 z-50 flex">
          <div className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white pb-12 shadow-xl dark:bg-gray-900 sm:max-w-sm">
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between bg-white/95 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:bg-gray-900/95 dark:supports-[backdrop-filter]:bg-gray-900/80 sm:px-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">Filters</h2>
              <button
                type="button"
                className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                onClick={() => setIsMobileFiltersOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <RiCloseLine className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </button>
            </div>

            {/* Filter content */}
            <div className="px-4 py-4 sm:px-6">
              {renderFilterContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop filters */}
      <div className="hidden lg:block">
        {renderFilterContent()}
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-40 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
          <div className="flex h-full items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent" />
          </div>
        </div>
      )}
    </>
  )
} 